<template>
  <div class="uptime-timeline h-full">
    <!-- Loading State -->
    <div v-if="loading" class="flex items-center justify-center h-full">
      <svg class="animate-spin w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
      </svg>
      <span class="ml-2 text-sm text-gray-500">Loading chart...</span>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="flex flex-col items-center justify-center h-full">
      <p class="text-sm text-red-600">
        {{ error }}
      </p>
      <button @click="fetchData" class="mt-2 text-sm text-blue-600 hover:underline">
        Retry
      </button>
    </div>

    <!-- Empty State -->
    <div v-else-if="!data.length" class="flex flex-col items-center justify-center h-full">
      <p class="text-sm text-gray-500">
        No data available
      </p>
    </div>

    <!-- Chart -->
    <div v-else class="chart-container h-full" ref="chartContainer">
      <svg :width="chartWidth" :height="chartHeight" class="chart-svg">
        <!-- Grid lines -->
        <g class="grid-lines">
          <line
            v-for="(tick, i) in yTicks"
            :key="'grid-' + i"
            :x1="padding.left"
            :y1="yScale(tick)"
            :x2="chartWidth - padding.right"
            :y2="yScale(tick)"
            stroke="var(--border-muted)"
            stroke-dasharray="4" />
        </g>

        <!-- Y-axis labels -->
        <g class="y-axis">
          <text
            v-for="(tick, i) in yTicks"
            :key="'y-' + i"
            :x="padding.left - 8"
            :y="yScale(tick)"
            text-anchor="end"
            dominant-baseline="middle"
            fill="var(--text-tertiary)"
            font-size="11">
            {{ formatMs(tick) }}
          </text>
        </g>

        <!-- Area fill -->
        <path
          :d="areaPath"
          :fill="areaFill"
          opacity="0.2" />

        <!-- Line -->
        <path
          :d="linePath"
          fill="none"
          :stroke="lineColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round" />

        <!-- Data points -->
        <g class="data-points">
          <circle
            v-for="(point, i) in dataPoints"
            :key="'point-' + i"
            :cx="point.x"
            :cy="point.y"
            :r="hoveredIndex === i ? 6 : 3"
            :fill="point.success ? 'var(--color-success-500)' : 'var(--color-error-500)'"
            :stroke="hoveredIndex === i ? 'white' : 'none'"
            stroke-width="2"
            class="cursor-pointer"
            @mouseenter="handleHover(i, $event)"
            @mouseleave="handleHoverEnd" />
        </g>

        <!-- X-axis labels -->
        <g class="x-axis">
          <text
            v-for="(tick, i) in xTicks"
            :key="'x-' + i"
            :x="tick.x"
            :y="chartHeight - 8"
            text-anchor="middle"
            fill="var(--text-tertiary)"
            font-size="11">
            {{ tick.label }}
          </text>
        </g>
      </svg>

      <!-- Tooltip -->
      <div
        v-if="tooltip.visible"
        class="chart-tooltip"
        :style="{ top: tooltip.top + 'px', left: tooltip.left + 'px' }">
        <div class="font-medium">
          {{ tooltip.time }}
        </div>
        <div :class="tooltip.success ? 'text-green-600' : 'text-red-600'">
          {{ tooltip.responseTime ? `${tooltip.responseTime}ms avg` : 'No data' }}
        </div>
        <div class="text-gray-500">
          {{ tooltip.uptimePercentage }}% uptime ({{ tooltip.totalChecks }} checks)
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, nextTick, watch } from 'vue'

export default {
  name: 'UptimeTimeline',

  props: {
    websiteId: {
      type: String,
      required: true
    },
    hours: {
      type: Number,
      default: 24
    },
    apiUrl: {
      type: String,
      required: true
    }
  },

  setup(props) {
    const loading = ref(true)
    const error = ref(null)
    const data = ref([])
    const chartContainer = ref(null)
    const chartWidth = ref(800)
    const chartHeight = ref(240)
    const hoveredIndex = ref(null)

    const padding = { top: 20, right: 20, bottom: 30, left: 50 }

    const tooltip = ref({
      visible: false,
      top: 0,
      left: 0,
      time: '',
      responseTime: 0,
      success: true,
      status: null,
      error: null
    })

    // Computed scales
    const yMax = computed(() => {
      const times = data.value
        .filter((d) => d.avg_response_time)
        .map((d) => d.avg_response_time)
      return times.length ? Math.max(...times, 200) * 1.1 : 1000
    })

    const yScale = (value) => {
      const innerHeight = chartHeight.value - padding.top - padding.bottom
      return chartHeight.value - padding.bottom - (value / yMax.value) * innerHeight
    }

    const xScale = (index) => {
      const innerWidth = chartWidth.value - padding.left - padding.right
      return padding.left + (index / (data.value.length - 1 || 1)) * innerWidth
    }

    const yTicks = computed(() => {
      const max = yMax.value
      const ticks = []
      const step = Math.ceil(max / 5 / 100) * 100

      for (let i = 0; i <= max; i += step) {
        ticks.push(i)
      }
      return ticks
    })

    const xTicks = computed(() => {
      if (!data.value.length) return []

      const ticks = []
      const step = Math.max(1, Math.floor(data.value.length / 6))

      for (let i = 0; i < data.value.length; i += step) {
        const item = data.value[i]
        const date = new Date(item.timestamp)
        ticks.push({
          x: xScale(i),
          label: date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
        })
      }

      return ticks
    })

    const dataPoints = computed(() => data.value.map((d, i) => ({
      x: xScale(i),
      y: yScale(d.avg_response_time || 0),
      success: d.uptime_percentage === 100,
      responseTime: d.avg_response_time,
      uptimePercentage: d.uptime_percentage,
      totalChecks: d.total_checks,
      time: d.timestamp
    })))

    const linePath = computed(() => {
      if (!dataPoints.value.length) return ''

      return dataPoints.value.reduce((path, point, i) => {
        const cmd = i === 0 ? 'M' : 'L'
        return `${path} ${cmd} ${point.x} ${point.y}`
      }, '')
    })

    const areaPath = computed(() => {
      if (!dataPoints.value.length) return ''

      const baseline = chartHeight.value - padding.bottom
      const line = linePath.value

      return `${line} L ${dataPoints.value[dataPoints.value.length - 1].x} ${baseline} L ${dataPoints.value[0].x} ${baseline} Z`
    })

    const lineColor = computed(() => {
      const failedCount = data.value.filter((d) => d.uptime_percentage < 100).length
      if (failedCount === 0) return 'var(--color-success-500)'
      if (failedCount / data.value.length < 0.1) return 'var(--color-warning-500)'
      return 'var(--color-error-500)'
    })

    const areaFill = computed(() => lineColor.value)

    const formatMs = (ms) => {
      if (ms >= 1000) return `${(ms / 1000).toFixed(1)}s`
      return `${Math.round(ms)}ms`
    }

    const handleHover = (index, _event) => {
      hoveredIndex.value = index
      const point = dataPoints.value[index]
      const rect = chartContainer.value?.getBoundingClientRect()

      if (!rect) return

      const date = new Date(point.time)

      tooltip.value = {
        visible: true,
        top: point.y - 60,
        left: point.x - 50,
        time: date.toLocaleString('en-US', {
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit'
        }),
        responseTime: point.responseTime ? Math.round(point.responseTime) : null,
        success: point.success,
        uptimePercentage: point.uptimePercentage,
        totalChecks: point.totalChecks
      }
    }

    const handleHoverEnd = () => {
      hoveredIndex.value = null
      tooltip.value.visible = false
    }

    const fetchData = async() => {
      loading.value = true
      error.value = null

      try {
        const response = await fetch(`${props.apiUrl}?hours=${props.hours}`, {
          headers: { 'Accept': 'application/json' }
        })

        if (!response.ok) {
          throw new Error('Failed to fetch data')
        }

        data.value = await response.json()

        // Update dimensions after data is loaded and DOM is updated
        await nextTick()
        updateDimensions()
      } catch (e) {
        console.error('[UptimeTimeline] Error:', e)
        error.value = e.message || 'Failed to load timeline data'
      } finally {
        loading.value = false
      }
    }

    const updateDimensions = () => {
      if (chartContainer.value) {
        const width = chartContainer.value.clientWidth || chartContainer.value.parentElement?.clientWidth || 800
        chartWidth.value = Math.max(width, 400)
        chartHeight.value = 220
      }
    }

    // Watch for container ref changes
    watch(chartContainer, (newVal) => {
      if (newVal) {
        nextTick(() => updateDimensions())
      }
    })

    onMounted(() => {
      fetchData()
      window.addEventListener('resize', updateDimensions)
    })

    onUnmounted(() => {
      window.removeEventListener('resize', updateDimensions)
    })

    return {
      loading,
      error,
      data,
      chartContainer,
      chartWidth,
      chartHeight,
      padding,
      hoveredIndex,
      tooltip,
      yScale,
      yTicks,
      xTicks,
      dataPoints,
      linePath,
      areaPath,
      lineColor,
      areaFill,
      formatMs,
      handleHover,
      handleHoverEnd,
      fetchData
    }
  }
}
</script>

<style scoped>
.uptime-timeline {
  position: relative;
  width: 100%;
}

.chart-container {
  position: relative;
  width: 100%;
}

.chart-svg {
  display: block;
  max-width: 100%;
}

.chart-tooltip {
  position: absolute;
  z-index: 50;
  padding: 8px 12px;
  font-size: 12px;
  line-height: 1.4;
  background: var(--bg-surface);
  border: 1px solid var(--border-secondary);
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  pointer-events: none;
  white-space: nowrap;
}

.data-points circle {
  transition: r 0.15s ease;
}
</style>
